import React, { useState, useEffect, useCallback } from 'react';
import './SocioDetalle.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

const extractList = (payload, extraKeys = []) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];

    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.data)) return payload.data;

    for (const key of extraKeys) {
        if (Array.isArray(payload[key])) return payload[key];
    }

    return [];
};

function SocioDetalle({ socio, token, onVolver, onEditar }) {
    const [activeSubTab, setActiveSubTab] = useState('datos');
    const [formaciones, setFormaciones] = useState([]);
    const [estudios, setEstudios] = useState([]);
    const [puestos, setPuestos] = useState([]);
    const [showFormacionForm, setShowFormacionForm] = useState(false);
    const [showEstudioForm, setShowEstudioForm] = useState(false);
    const [showPuestoForm, setShowPuestoForm] = useState(false);

    const fetchFormaciones = useCallback(async () => {
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/formacion-academica/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.detail || 'No se pudieron cargar las formaciones');
            }

            setFormaciones(extractList(data, ['formaciones', 'formaciones_academicas']));
        } catch (err) {
            console.error('Error al cargar formaciones:', err);
            setFormaciones([]);
        }
    }, [socio?.id, token]);

    const fetchEstudios = useCallback(async () => {
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/estudios/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.detail || 'No se pudieron cargar los estudios');
            }

            setEstudios(extractList(data, ['estudios', 'estudios_relacionados']));
        } catch (err) {
            console.error('Error al cargar estudios:', err);
            setEstudios([]);
        }
    }, [socio?.id, token]);

    const fetchPuestos = useCallback(async () => {
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/puestos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.detail || 'No se pudieron cargar los puestos');
            }

            setPuestos(extractList(data, ['puestos', 'puestos_desempenados']));
        } catch (err) {
            console.error('Error al cargar puestos:', err);
            setPuestos([]);
        }
    }, [socio?.id, token]);

    useEffect(() => {
        if (activeSubTab === 'formacion') fetchFormaciones();
        if (activeSubTab === 'estudios') fetchEstudios();
        if (activeSubTab === 'puestos') fetchPuestos();
    }, [activeSubTab, fetchFormaciones, fetchEstudios, fetchPuestos]);

    const renderDatosPersonales = () => {
        // Construir URL de la foto si existe
        const avatarUrl = socio.avatar
            ? (socio.avatar.startsWith('http')
                ? socio.avatar
                : `http://localhost:8000/media/${socio.avatar}`)
            : null;

        return (
            <div>
                <h4>Datos Personales</h4>

                {/* Mostrar foto del socio si existe */}
                {avatarUrl && (
                    <div className="avatar-container">
                        <img
                            src={avatarUrl}
                            alt={`${socio.nombres} ${socio.apellidos}`}
                            className="avatar-img"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                console.error('Error al cargar imagen:', socio.avatar);
                            }}
                        />
                    </div>
                )}

                <p><strong>RegABIS:</strong> {socio.reg_abis || 'N/A'}</p>
                <p><strong>Apellidos:</strong> {socio.apellidos || 'N/A'}</p>
                <p><strong>Nombres:</strong> {socio.nombres || 'N/A'}</p>
                <p><strong>Fecha de Nacimiento:</strong> {socio.fecha_nac || 'N/A'}</p>
                <p><strong>Lugar de Nacimiento:</strong> {socio.lugar_nac || 'N/A'}</p>
                <p><strong>Ciudad:</strong> {socio.ciudad || 'N/A'}</p>
                <p><strong>Nacionalidad:</strong> {socio.nacionalidad || 'N/A'}</p>
                <p><strong>CI:</strong> {socio.ci || 'N/A'} {socio.complemento || ''} ({socio.expedido || 'N/A'})</p>
                <p><strong>Teléfono:</strong> {socio.telefono || 'N/A'}</p>
                <p><strong>Celular:</strong> {socio.celular || 'N/A'}</p>
                <p><strong>Email:</strong> {socio.email || 'N/A'}</p>
                <p><strong>Domicilio:</strong> {socio.domicilio || 'N/A'}</p>
                <p><strong>N° Registro Profesional:</strong> {socio.nreg_profesional || 'N/A'}</p>
            </div>
        );
    };

    const renderFormaciones = () => (
        <div>
            <h4>Formación Académica</h4>
            <DashboardButton
                variant={showFormacionForm ? 'secondary' : 'success'}
                onClick={() => setShowFormacionForm(!showFormacionForm)}
            >
                {showFormacionForm ? 'Cancelar' : 'Agregar Formación'}
            </DashboardButton>

            {showFormacionForm && <FormacionForm socio={socio} token={token} onSuccess={() => { setShowFormacionForm(false); fetchFormaciones(); }} />}

            {formaciones.length === 0 && <p>No hay formaciones registradas.</p>}
            {formaciones.map(f => (
                <FormacionItem key={f.id} formacion={f} socio={socio} token={token} onUpdate={fetchFormaciones} />
            ))}
        </div>
    );

    const renderEstudios = () => (
        <div>
            <h4>Estudios Relacionados</h4>
            <DashboardButton
                variant={showEstudioForm ? 'secondary' : 'success'}
                onClick={() => setShowEstudioForm(!showEstudioForm)}
            >
                {showEstudioForm ? 'Cancelar' : 'Agregar Estudio'}
            </DashboardButton>

            {showEstudioForm && <EstudioForm socio={socio} token={token} onSuccess={() => { setShowEstudioForm(false); fetchEstudios(); }} />}

            {estudios.length === 0 && <p>No hay estudios registrados.</p>}
            {estudios.map(e => (
                <EstudioItem key={e.id} estudio={e} socio={socio} token={token} onUpdate={fetchEstudios} />
            ))}
        </div>
    );

    const renderPuestos = () => (
        <div>
            <h4>Puestos Desempeñados</h4>
            <DashboardButton
                variant={showPuestoForm ? 'secondary' : 'success'}
                onClick={() => setShowPuestoForm(!showPuestoForm)}
            >
                {showPuestoForm ? 'Cancelar' : 'Agregar Puesto'}
            </DashboardButton>

            {showPuestoForm && <PuestoForm socio={socio} token={token} onSuccess={() => { setShowPuestoForm(false); fetchPuestos(); }} />}

            {puestos.length === 0 && <p>No hay puestos registrados.</p>}
            {puestos.map(p => (
                <PuestoItem key={p.id} puesto={p} socio={socio} token={token} onUpdate={fetchPuestos} />
            ))}
        </div>
    );

    return (
        <div>
            <h3>Detalle del Socio: {socio.apellidos} {socio.nombres}</h3>
            <div className="detail-actions">
                {onVolver && <DashboardButton onClick={onVolver} variant="view">Volver</DashboardButton>}
                <DashboardButton onClick={onEditar} variant="warning">Editar Datos Personales</DashboardButton>
            </div>

            <div className="subtabs">
                <button onClick={() => setActiveSubTab('datos')} className={`subtab-btn ${activeSubTab === 'datos' ? 'active' : ''}`}>
                    Datos Personales
                </button>
                <button onClick={() => setActiveSubTab('formacion')} className={`subtab-btn ${activeSubTab === 'formacion' ? 'active' : ''}`}>
                    Formación Académica
                </button>
                <button onClick={() => setActiveSubTab('estudios')} className={`subtab-btn ${activeSubTab === 'estudios' ? 'active' : ''}`}>
                    Estudios Relacionados
                </button>
                <button onClick={() => setActiveSubTab('puestos')} className={`subtab-btn ${activeSubTab === 'puestos' ? 'active' : ''}`}>
                    Puestos Desempeñados
                </button>
            </div>

            <div>
                {activeSubTab === 'datos' && <div className="subtab-content active">{renderDatosPersonales()}</div>}
                {activeSubTab === 'formacion' && <div className="subtab-content active">{renderFormaciones()}</div>}
                {activeSubTab === 'estudios' && <div className="subtab-content active">{renderEstudios()}</div>}
                {activeSubTab === 'puestos' && <div className="subtab-content active">{renderPuestos()}</div>}
            </div>
        </div>
    );
}

// Item de Formación Académica con edición y eliminación
function FormacionItem({ formacion, socio, token, onUpdate }) {
    const [editing, setEditing] = useState(false);

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar esta formación académica?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/formacion-academica/${formacion.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Formación eliminada');
                onUpdate();
            } else {
                alert('Error al eliminar');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    if (editing) {
        return <FormacionForm socio={socio} token={token} formacion={formacion} onSuccess={() => { setEditing(false); onUpdate(); }} />;
    }

    return (
        <div className="item-card">
            <p><strong>Institución:</strong> {formacion.institucion}</p>
            <p><strong>Título Académico:</strong> {formacion.titulo_acad} ({formacion.n_titulo_acad}) - Año: {formacion.anio}</p>
            <p><strong>Título Profesional:</strong> {formacion.titulo_pro} ({formacion.n_titulo_pro}) - Año: {formacion.anio_tp}</p>
            <p><strong>Lugar:</strong> {formacion.ciudad}, {formacion.pais}</p>
            <div className="item-actions">
                <DashboardButton onClick={() => setEditing(true)} variant="warning" size="sm">Editar</DashboardButton>
                <DashboardButton onClick={handleDelete} variant="danger" size="sm">Eliminar</DashboardButton>
            </div>
        </div>
    );
}

// Formulario para agregar/editar Formación Académica
function FormacionForm({ socio, token, formacion, onSuccess }) {
    const [formData, setFormData] = useState({
        institucion: formacion?.institucion || '',
        titulo_acad: formacion?.titulo_acad || '',
        n_titulo_acad: formacion?.n_titulo_acad || '',
        anio: formacion?.anio || '',
        titulo_pro: formacion?.titulo_pro || '',
        n_titulo_pro: formacion?.n_titulo_pro || '',
        anio_tp: formacion?.anio_tp || '',
        ciudad: formacion?.ciudad || '',
        pais: formacion?.pais || ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = formacion
                ? `http://localhost:8000/api/socios/${socio.id}/formacion-academica/${formacion.id}/`
                : `http://localhost:8000/api/socios/${socio.id}/formacion-academica/`;
            const method = formacion ? 'PATCH' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                alert(formacion ? 'Formación actualizada' : 'Formación agregada');
                onSuccess();
            } else {
                alert('Error al guardar formación');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-card">
            <input name="institucion" placeholder="Institución" value={formData.institucion} onChange={handleChange} required className="form-input" />
            <input name="titulo_acad" placeholder="Título Académico" value={formData.titulo_acad} onChange={handleChange} required className="form-input" />
            <input name="n_titulo_acad" placeholder="N° Título Académico" value={formData.n_titulo_acad} onChange={handleChange} className="form-input" />
            <input name="anio" type="number" placeholder="Año" value={formData.anio} onChange={handleChange} className="form-input" />
            <input name="titulo_pro" placeholder="Título Profesional" value={formData.titulo_pro} onChange={handleChange} className="form-input" />
            <input name="n_titulo_pro" placeholder="N° Título Profesional" value={formData.n_titulo_pro} onChange={handleChange} className="form-input" />
            <input name="anio_tp" type="number" placeholder="Año Título Profesional" value={formData.anio_tp} onChange={handleChange} className="form-input" />
            <input name="ciudad" placeholder="Ciudad" value={formData.ciudad} onChange={handleChange} className="form-input" />
            <input name="pais" placeholder="País" value={formData.pais} onChange={handleChange} className="form-input" />
            <DashboardButton type="submit" variant="success">Guardar</DashboardButton>
        </form>
    );
}

// Item de Estudio Relacionado con edición y eliminación
function EstudioItem({ estudio, socio, token, onUpdate }) {
    const [editing, setEditing] = useState(false);

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar este estudio?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/estudios/${estudio.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Estudio eliminado');
                onUpdate();
            } else {
                alert('Error al eliminar');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    if (editing) {
        return <EstudioForm socio={socio} token={token} estudio={estudio} onSuccess={() => { setEditing(false); onUpdate(); }} />;
    }

    return (
        <div className="item-card">
            <p><strong>Curso:</strong> {estudio.nombre_curso}</p>
            <p><strong>Institución:</strong> {estudio.institucion}</p>
            <p><strong>Periodo:</strong> {estudio.finicio} - {estudio.ffinal}</p>
            <p><strong>Lugar:</strong> {estudio.ciudad}, {estudio.pais}</p>
            <p><strong>Año:</strong> {estudio.anio}</p>
            <div className="item-actions">
                <DashboardButton onClick={() => setEditing(true)} variant="warning" size="sm">Editar</DashboardButton>
                <DashboardButton onClick={handleDelete} variant="danger" size="sm">Eliminar</DashboardButton>
            </div>
        </div>
    );
}

// Formulario para agregar/editar Estudio Relacionado
function EstudioForm({ socio, token, estudio, onSuccess }) {
    const [formData, setFormData] = useState({
        institucion: estudio?.institucion || '',
        nombre_curso: estudio?.nombre_curso || '',
        finicio: estudio?.finicio || '',
        ffinal: estudio?.ffinal || '',
        ciudad: estudio?.ciudad || '',
        pais: estudio?.pais || '',
        anio: estudio?.anio || ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = estudio
                ? `http://localhost:8000/api/socios/${socio.id}/estudios/${estudio.id}/`
                : `http://localhost:8000/api/socios/${socio.id}/estudios/`;
            const method = estudio ? 'PATCH' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                alert(estudio ? 'Estudio actualizado' : 'Estudio agregado');
                onSuccess();
            } else {
                alert('Error al guardar estudio');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-card">
            <input name="nombre_curso" placeholder="Nombre del Curso" value={formData.nombre_curso} onChange={handleChange} required className="form-input" />
            <input name="institucion" placeholder="Institución" value={formData.institucion} onChange={handleChange} required className="form-input" />
            <input name="finicio" type="date" placeholder="Fecha Inicio" value={formData.finicio} onChange={handleChange} className="form-input" />
            <input name="ffinal" type="date" placeholder="Fecha Fin" value={formData.ffinal} onChange={handleChange} className="form-input" />
            <input name="ciudad" placeholder="Ciudad" value={formData.ciudad} onChange={handleChange} className="form-input" />
            <input name="pais" placeholder="País" value={formData.pais} onChange={handleChange} className="form-input" />
            <input name="anio" type="number" placeholder="Año" value={formData.anio} onChange={handleChange} className="form-input" />
            <DashboardButton type="submit" variant="success">Guardar</DashboardButton>
        </form>
    );
}

// Item de Puesto Desempeñado con edición y eliminación
function PuestoItem({ puesto, socio, token, onUpdate }) {
    const [editing, setEditing] = useState(false);

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar este puesto?')) return;
        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socio.id}/puestos/${puesto.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Puesto eliminado');
                onUpdate();
            } else {
                alert('Error al eliminar');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    if (editing) {
        return <PuestoForm socio={socio} token={token} puesto={puesto} onSuccess={() => { setEditing(false); onUpdate(); }} />;
    }

    return (
        <div className="item-card">
            <p><strong>Puesto:</strong> {puesto.puesto}</p>
            <p><strong>Institución/Empresa:</strong> {puesto.institucion_empresa}</p>
            <p><strong>Unidad/Proyecto:</strong> {puesto.unidad_proy}</p>
            <p><strong>Periodo:</strong> {puesto.desde} - {puesto.hasta || 'Actualidad'}</p>
            <div className="item-actions">
                <DashboardButton onClick={() => setEditing(true)} variant="warning" size="sm">Editar</DashboardButton>
                <DashboardButton onClick={handleDelete} variant="danger" size="sm">Eliminar</DashboardButton>
            </div>
        </div>
    );
}

// Formulario para agregar/editar Puesto Desempeñado
function PuestoForm({ socio, token, puesto, onSuccess }) {
    const [formData, setFormData] = useState({
        puesto: puesto?.puesto || '',
        institucion_empresa: puesto?.institucion_empresa || '',
        unidad_proy: puesto?.unidad_proy || '',
        desde: puesto?.desde || '',
        hasta: puesto?.hasta || ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = puesto
                ? `http://localhost:8000/api/socios/${socio.id}/puestos/${puesto.id}/`
                : `http://localhost:8000/api/socios/${socio.id}/puestos/`;
            const method = puesto ? 'PATCH' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                alert(puesto ? 'Puesto actualizado' : 'Puesto agregado');
                onSuccess();
            } else {
                alert('Error al guardar puesto');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-card">
            <input name="puesto" placeholder="Puesto" value={formData.puesto} onChange={handleChange} required className="form-input" />
            <input name="institucion_empresa" placeholder="Institución/Empresa" value={formData.institucion_empresa} onChange={handleChange} required className="form-input" />
            <input name="unidad_proy" placeholder="Unidad/Proyecto" value={formData.unidad_proy} onChange={handleChange} className="form-input" />
            <input name="desde" type="date" placeholder="Desde" value={formData.desde} onChange={handleChange} required className="form-input" />
            <input name="hasta" type="date" placeholder="Hasta (dejar vacío si es actual)" value={formData.hasta} onChange={handleChange} className="form-input" />
            <DashboardButton type="submit" variant="success">Guardar</DashboardButton>
        </form>
    );
}

export default SocioDetalle;
